import React, { useState, useEffect, useCallback, useMemo, Dispatch, SetStateAction } from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { unleashFeatureFlagEnabled } from '@scale/frontend-lib/featureFlag';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { Event, logEvent } from '@scale/frontend-lib/events';
import API from '@scale/frontend-lib/dashboard-api';
import { IUserJson } from '@scale/shared/types/user';
import { IInstructionJson, TaskerQuizEntryProps } from '@scale/shared/projectInstruction/types';
import logger from '@scale/frontend-lib/logging';
import { InstructionComponentType, AccountType } from '@scale/shared/defaults';
import { dataNormalizer } from './utils';
import {
  NormalizedComponentProps,
  ExtraAlertsProps,
  SelectedPathType,
  SearchResultItemProps,
  ComponentJson,
} from './types';
import { TOOL_TUTORIALS } from './constants';
import { quizTimerHooks } from './quiz/utils';
import { IOntologyChoice } from '@scale/shared/types/ontology';
import apiFetch from 'react-scale/lib/api-fetch';
import { checkSpecializationsConfig } from '@scale/shared/task_lint/linters/categorization';

export interface InstructionV2ResProps {
  ok: boolean;
  instruction: IInstructionJson;
  diffInstructions?: IInstructionJson;
  firstSeenProjectInstruction: boolean;
  hasSensitiveContent: boolean;
  param_chunks: boolean;
  title?: string; // title is not necessary in preview mode
  shouldUseNGI?: boolean;
  quiz?: TaskerQuizEntryProps;
  hasVersionComparison?: boolean;
}
interface ProjectInstructionContextType {
  quiz: undefined | TaskerQuizEntryProps;
  setQuiz: Dispatch<SetStateAction<TaskerQuizEntryProps | undefined>>;
  title: string;
  enableDiff: boolean;
  enableQuiz: boolean;
  extraAlerts: ExtraAlertsProps;
  enableSidePanel: boolean;
  hasVersionComparison: boolean;
  shouldUseNGI: boolean;
  handleStart: () => void;
  openQuiz: boolean;
  setEnableDiff: Dispatch<SetStateAction<boolean>>;
  setOpenQuiz: Dispatch<SetStateAction<boolean>>;
  startButtonText?: string;
  selectedSection: any;
  selectedPath: SelectedPathType;
  setSelectedPath: (_: SelectedPathType) => void;
  searchResult: Array<{ item: SearchResultItemProps; matches: any }> | null;
  setSearchResult: Dispatch<SetStateAction<{ item: SearchResultItemProps; matches: any }[] | null>>;
  components: NormalizedComponentProps[];
  setComponents: Dispatch<SetStateAction<NormalizedComponentProps[]>>;
  showingInstructionsFn: (_: boolean) => void;
  setShowingInstructionsFn: (_: Dispatch<SetStateAction<boolean>>) => void;
  isMobileView: boolean;
  setIsMobileView: Dispatch<SetStateAction<boolean>>;
  disableScrollOnMount?: boolean;
  scrollIntoView: number | number[];
  scrollToggle: boolean;
  isLoading: boolean;
  isEmbedded?: boolean;
  isCorpUser?: boolean;
  isOnboarding?: boolean;
  isRapidPreview?: boolean;
  typesGroup: Array<InstructionComponentType | 'ToolTutorials'>;
  timeSpentOnQuiz: number;
  projectId: string;
  projectInstruction: InstructionV2ResProps | null;
  customStartButton?: React.Component;
  doNotRenderChildrenSet?: Set<string>;
  disableSidePanel?: boolean;
}
const ProjectInstructionContext = React.createContext<ProjectInstructionContextType>({
  quiz: undefined,
  setQuiz: (_: any) => {},
  title: '',
  enableDiff: true,
  enableQuiz: false,
  extraAlerts: { hasSensitiveContent: false, isEdgeCase: false, param_chunks: false },
  enableSidePanel: false,
  hasVersionComparison: false,
  shouldUseNGI: false,
  handleStart: () => null,
  openQuiz: false,
  setEnableDiff: (_: any) => null,
  setOpenQuiz: (_: any) => {},
  startButtonText: '',
  selectedSection: null,
  selectedPath: [],
  setSelectedPath: (_: SelectedPathType) => null,
  searchResult: null,
  setSearchResult: (_: any) => null,
  components: [],
  setComponents: () => null,
  showingInstructionsFn: (_: boolean) => null,
  setShowingInstructionsFn: (_: Dispatch<SetStateAction<boolean>>) => {},
  isMobileView: false,
  setIsMobileView: (_: any) => null,
  disableScrollOnMount: false,
  scrollIntoView: -1,
  scrollToggle: false,
  isLoading: false,
  isEmbedded: false,
  isCorpUser: false,
  isOnboarding: false,
  isRapidPreview: false,
  typesGroup: [],
  timeSpentOnQuiz: 0,
  projectId: '',
  projectInstruction: null,
  customStartButton: undefined,
  doNotRenderChildrenSet: undefined,
});

export const useProjectInstructionContext = () => React.useContext(ProjectInstructionContext);

let timer: any = null;
export const ProjectInstructionProvider = React.memo(
  ({
    projectId: projectIdFromProps,
    assignmentId: assignmentIdFromProps,
    startButtonText,
    isEmbedded,
    isMobileView: initIsMobileView,
    startTasks,
    user,
    userIsCustomer,
    isEdgeCase,
    isRapidPreview,
    instructionV2Res,
    children,
    customStartButton,
    disableDiff,
    disableScrollOnMount,
    disableSidePanel,
  }: {
    projectId?: string;
    assignmentId?: string;
    isEmbedded?: boolean; // embedded here means not full page mode
    isMobileView?: boolean;
    user?: IUserJson;
    userIsCustomer?: boolean;
    isEdgeCase?: boolean;
    startTasks?: () => void;
    startButtonText?: string;
    isRapidPreview?: boolean; // customer facing instruction in rapid end. 1. it won't group nav types 2. no project name in title
    instructionV2Res?: InstructionV2ResProps;
    customStartButton?: React.Component;
    disableDiff?: boolean;
    disableScrollOnMount?: boolean;
    children: React.ReactElement;
    disableSidePanel?: boolean;
  }) => {
    const { query, pathname } = useRouter();
    // we get componentId and type from contextual trigger, where we open the instruction with taxonomy
    const {
      id: projectIdFromQuery,
      componentId,
      type,
      assignmentId,
      taskId,
    } = query as Record<string, string>;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [projectInstruction, setProjectInstruction] = useState<InstructionV2ResProps | null>(
      null,
    );
    const [quiz, setQuiz] = useState<TaskerQuizEntryProps | undefined>();
    const [timeStarted, setTimeStarted] = useState(0);
    const [enableSidePanel, setEnableSidePanel] = useState(false);
    const [openQuiz, setOpenQuiz] = useState(false);
    const [enableQuiz, setEnableQuiz] = useState<boolean>(false);
    const [timeSpentOnQuiz, setTimeSpentOnQuiz] = useState<number>(0);
    const [isOnboarding, setIsOnboarding] = useState<boolean>(false);
    const [isMobileView, setIsMobileView] = useState(initIsMobileView ?? false);
    const [showingInstructionsFn, setShowingInstructionsFn] = useState<(_: boolean) => void>(
      (_: boolean) => {},
    );
    const [searchResult, setSearchResult] = useState<Array<{
      item: SearchResultItemProps;
      matches: any;
    }> | null>(null);
    const [components, setComponents] = useState<NormalizedComponentProps[]>([]);
    const [extraAlerts, setExtraAlerts] = useState<ExtraAlertsProps>({ isEdgeCase });
    const [selectedPath, setSelectedPath] = useState<SelectedPathType>([
      type as InstructionComponentType,
      componentId,
    ]);
    const [title, setTitle] = useState<string>('');
    const [hasVersionComparison, setHasVersionComparison] = useState(false);
    const [scrollIntoView, setScrollIntoView] = useState<number | number[]>(-1);
    const [scrollToggle, setScrollToggle] = useState(false);
    const [enableDiff, setEnableDiff] = useState<boolean>(!disableDiff);
    const useInstructionLimits = useUnleashFeatureFlag('use-rapid-instruction-limits', {}, false);
    const isCorpUser = !!(user?.spoofed || (user?.email && user?.email.endsWith('scale.com')));
    const isCustomer: boolean =
      !!userIsCustomer || user?.isCorporateUser || user?.accountType === AccountType.User;
    const projectId = projectIdFromProps ?? projectIdFromQuery;

    useEffect(() => {
      if (initIsMobileView !== undefined) {
        setIsMobileView(initIsMobileView);
      }
    }, [initIsMobileView]);

    useEffect(() => {
      setTimeStarted(new Date().getTime());
      fetchShowNewInstruction(enableDiff);
      (async () => {
        const enableSidePanelFlag = await unleashFeatureFlagEnabled({
          feature: 'use-instruction-side-panel',
        });
        const isInTasksPage = pathname.includes('/tasks');
        const isInTrainingPage = pathname.includes('/training');
        setEnableSidePanel(
          !!(enableSidePanelFlag && projectIdFromProps && (isInTasksPage || isInTrainingPage)) &&
            !disableSidePanel,
        );
      })();
    }, [projectId, enableDiff, instructionV2Res]);

    const getWorkerSpecializations = useCallback(async () => {
      if (projectId) {
        try {
          const { tme: taskerMetricsEntry } = await apiFetch(
            null,
            `corp-api/remedialCourses/findTaskerMetricsEntry`,
            {
              method: 'POST',
              body: {
                worker: user?.id,
                project: projectId,
              },
            },
          );

          return taskerMetricsEntry?.specializations ?? [];
        } catch (error) {
          return [];
        }
      } else {
        try {
          const workerSpecialization = await apiFetch(
            null,
            `corp-api/remedialCourses/findTaskerSpecializations`,
            {
              method: 'POST',
              body: {
                worker: user?.id,
                taskId,
              },
            },
          );

          return workerSpecialization;
        } catch (error) {
          return [];
        }
      }
    }, [projectId, taskId, user]);

    const doNotRenderChildrenSet: Set<string> = useMemo(() => {
      const children = new Set<string>();
      components.forEach(component => {
        if (component.nodeType === InstructionComponentType.Label) {
          component.content?.nestedAttributeIds?.forEach((nestedAttrId: string) => {
            children.add(nestedAttrId);
          });
          component.content?.nestedLabelIds?.forEach((nestedLabelId: string) => {
            children.add(nestedLabelId);
          });
        }
      });
      return children;
    }, [components]); // maybe make this a fn call on creating/removing subComponents

    useEffect(() => {
      async function filterComponents() {
        const workerSpecialization = await getWorkerSpecializations();

        const filteredComponents = components.filter(component => {
          if ((component.content?.specializations ?? []).length > 0) {
            return checkSpecializationsConfig(
              workerSpecialization,
              component.content?.specializations!,
            );
          }

          return true;
        });

        if (components.length !== filteredComponents.length) setComponents(filteredComponents);
      }
      filterComponents();
    }, [getWorkerSpecializations, components]);

    const setRapidPreviewSelectedPath = (selectedPath: any) => {
      const [type, id] = selectedPath;
      if (components) {
        if (_.isEmpty(selectedPath)) {
          setScrollIntoView(-1);
        } else {
          for (let i = 0; i < components.length; i++) {
            const comp = components[i];
            if (comp.nodeType === type && comp.nodeId === id) {
              setScrollToggle(!scrollToggle);
              setScrollIntoView(i);
              break;
            } else if (comp.nodeType === InstructionComponentType.Group && comp.components) {
              for (let j = 0; j < comp.components.length; j++) {
                const section = comp.components[j];
                if (section.nodeType === type && section.nodeId === id) {
                  setScrollToggle(!scrollToggle);
                  setScrollIntoView([i, j]);
                  break;
                }
              }
            }
          }
        }
      }
    };
    const fetchShowNewInstruction = useCallback(
      async (enableDiff: any) => {
        setIsLoading(true);
        try {
          if (!instructionV2Res) {
            instructionV2Res = await API.selfserve.getProjectInstructionStatus(
              projectId,
              isRapidPreview ? undefined : assignmentIdFromProps || assignmentId, // if rapid preview, show project-level always
            );
          }

          if (!instructionV2Res) {
            return;
          }
          if (
            instructionV2Res.ok &&
            instructionV2Res.instruction &&
            instructionV2Res.instruction.components &&
            instructionV2Res.instruction.components.length > 0
          ) {
            setProjectInstruction(instructionV2Res);
            const {
              instruction,
              diffInstructions,
              firstSeenProjectInstruction,
              hasSensitiveContent,
              param_chunks,
              title,
              quiz: generatedQuiz,
            } = instructionV2Res;

            extraAlerts.hasSensitiveContent = hasSensitiveContent;
            extraAlerts.param_chunks = param_chunks;
            setEnableQuiz(!!generatedQuiz);
            setExtraAlerts({ ...extraAlerts });
            setTitle(title || '');
            if (generatedQuiz) {
              setQuiz(generatedQuiz);
            }
            setHasVersionComparison(diffInstructions !== undefined);

            const isOnboardingFlag = !!(firstSeenProjectInstruction && isEmbedded);

            let instructionComponents = dataNormalizer({
              components: diffInstructions
                ? enableDiff
                  ? diffInstructions.components
                  : instruction.components
                : instruction.components,
              isOnboarding: isOnboardingFlag,
              isCustomer,
            });
            try {
              const instructionComponentsOntology: NormalizedComponentProps[] = [];
              for (const component of instructionComponents) {
                const maybeOntologyProjectId = (component.content as ComponentJson)
                  .ontologyProjectId;
                if (!maybeOntologyProjectId) {
                  instructionComponentsOntology.push(component);
                } else {
                  const { ontology } = await apiFetch<{ ontology: IOntologyChoice[] }>(
                    null,
                    `/internal/ontology_manager/latest_ontology/${maybeOntologyProjectId}`,
                    {
                      method: 'GET',
                    },
                  );
                  const convertOntologyToComponents = (
                    ontology: IOntologyChoice[],
                  ): NormalizedComponentProps[] =>
                    _.sortBy(
                      ontology.map(choice => {
                        return {
                          nodeName: choice.display!,
                          nodeId: choice.value!,
                          nodeType: InstructionComponentType.Field,
                          iconName: 'info',
                          iconColor: 'blue',
                          content: {
                            _id: choice.value!,
                            type: InstructionComponentType.Field,
                            title: choice.display!,
                            description: choice.description || '',
                          },
                          components: convertOntologyToComponents(choice.subchoices || []),
                        };
                      }),
                      a => a.nodeName,
                    );
                  const componentsFromOntology = convertOntologyToComponents(ontology);

                  instructionComponentsOntology.push({
                    ...component,
                    components: componentsFromOntology,
                  });
                }
              }
              instructionComponents = instructionComponentsOntology;
            } catch (error) {
              logger.warn('ProjectInstructionProvider ', error);
            }
            setComponents(instructionComponents);
            setIsOnboarding(isOnboardingFlag);
          }
        } catch (error) {
          logger.warn('ProjectInstructionProvider ', error);
        }
        setIsLoading(false);
      },
      [projectId, isRapidPreview],
    );

    const handleStart = () => {
      async function updateUser() {
        const instructionObjectId = _.get(projectInstruction, ['instruction', '_id']);
        if (instructionObjectId) {
          const timeSpentSecs = Math.round((new Date().getTime() - timeStarted) / 1000);
          await API.selfserve.setUserHasSeenInstructions({
            instructionObjectId,
            timeSpentSecs,
            usingInstructionRestrictions: useInstructionLimits,
          });

          const data = {
            isNewInstruction: true,
            isOnboardingFlow: isOnboarding,
            isDuringTasking: false,
            openTabButtonClicked: false,
            startTime: timeStarted,
            closeTime: new Date().getTime(),
          };

          await logEvent(Event.InstructionRenderRecord, data);
        }
      }

      if (startTasks) {
        startTasks();
        updateUser();
      }
    };

    const handleTimer = () => {
      if (_.isEmpty(selectedPath)) {
        return;
      }

      let index: number = -1;
      let groupComponentIndex: number[] = [-1, -1];
      const [type, id] = selectedPath;
      components.forEach((comp, idx) => {
        if (comp.nodeType === type && comp.nodeId === id) {
          index = idx;
        } else if (comp.nodeType === InstructionComponentType.Group && comp.components) {
          comp.components.forEach((groupComp, groupCompIdx) => {
            if (groupComp.nodeType === type && groupComp.nodeId === id) {
              groupComponentIndex = [idx, groupCompIdx];
            }
          });
        }
      });

      clearInterval(timer);
      if (index >= 0) {
        const waitSeconds = components[index].waitSeconds;
        if (waitSeconds) {
          timer = setInterval(() => {
            const copiedComponents = [...components];

            // @ts-ignore
            copiedComponents[index].waitSeconds -= 1;

            if (copiedComponents[index].waitSeconds === 0) {
              clearInterval(timer);
              copiedComponents[index].waitSeconds = null;
            }
            setComponents(copiedComponents);
          }, 1000);
        }
      } else if (groupComponentIndex[0] > -1 && groupComponentIndex[1] > -1) {
        const [compIndex, sectionIndex] = groupComponentIndex;
        const groupComponentSections = components[compIndex].components;
        if (!groupComponentSections) {
          return;
        }

        const waitSeconds = groupComponentSections[sectionIndex].waitSeconds;

        if (waitSeconds) {
          timer = setInterval(() => {
            const copiedComponents = [...components];
            const copiedGroupComponentSections = copiedComponents[compIndex].components;

            if (!copiedGroupComponentSections) {
              return;
            }

            // @ts-ignore
            copiedGroupComponentSections[sectionIndex].waitSeconds -= 1;

            if (copiedGroupComponentSections[sectionIndex].waitSeconds === 0) {
              clearInterval(timer);
              copiedGroupComponentSections[sectionIndex].waitSeconds = null;
            }
            setComponents(copiedComponents);
          }, 1000);
        }
      }
    };

    const selectedSection = useMemo(() => {
      const [type, id] = selectedPath;

      if (components?.length) {
        if (_.isEmpty(_.compact(selectedPath))) {
          return isMobileView || (enableSidePanel && projectIdFromProps)
            ? null
            : { ...components[0].content, waitSeconds: components[0].waitSeconds };
        } else {
          for (const comp of components) {
            if (comp.nodeType === type && comp.nodeId === id) {
              return { ...comp.content, waitSeconds: comp.waitSeconds };
            } else if (comp.nodeType === InstructionComponentType.Group && comp.components) {
              for (const groupComp of comp.components) {
                if (groupComp.nodeType === type && groupComp.nodeId === id) {
                  return { ...groupComp.content, waitSeconds: groupComp.waitSeconds };
                }
              }
            }
          }
        }
      }

      return null;
    }, [selectedPath, components, isMobileView]);

    useEffect(() => {
      if (selectedSection) {
        handleTimer();
      }
    }, [selectedSection]);

    /*
        handle quiz timer
     */
    const { startQuizTimer, pauseQuizTimer } = useMemo(() => {
      return quizTimerHooks(setTimeSpentOnQuiz);
    }, [setTimeSpentOnQuiz]);

    useEffect(() => {
      if (openQuiz) {
        startQuizTimer();
      } else {
        pauseQuizTimer();
      }
    }, [openQuiz]);

    const typesGroup: Array<InstructionComponentType | 'ToolTutorials'> = [];

    if (!isEmbedded && !isRapidPreview) {
      // we don't group types when onboarding
      components?.forEach(({ nodeType }) => {
        if (!typesGroup.includes(nodeType)) {
          typesGroup.push(nodeType);
        }
      });
    } else if (!isCustomer) {
      typesGroup.push(TOOL_TUTORIALS);
    }

    return (
      <ProjectInstructionContext.Provider
        value={{
          quiz,
          setQuiz,
          title,
          enableQuiz,
          enableDiff,
          extraAlerts,
          enableSidePanel,
          projectInstruction,
          shouldUseNGI: !!projectInstruction?.shouldUseNGI,
          hasVersionComparison,
          handleStart,
          projectId,
          openQuiz,
          setOpenQuiz,
          startButtonText,
          showingInstructionsFn,
          setEnableDiff,
          setShowingInstructionsFn,
          selectedSection,
          selectedPath,
          setSelectedPath: isRapidPreview ? setRapidPreviewSelectedPath : setSelectedPath,
          searchResult,
          setSearchResult,
          timeSpentOnQuiz,
          components,
          setComponents,
          isMobileView: enableSidePanel || isMobileView, // both side panel and moibile view need small screen design
          setIsMobileView,
          disableScrollOnMount,
          scrollIntoView,
          scrollToggle,
          isLoading,
          isEmbedded,
          isCorpUser,
          isOnboarding,
          // with projectIdFromProps passed from withInstruction, we can make sure that it is sidePanel style
          isRapidPreview,
          typesGroup,
          customStartButton,
          doNotRenderChildrenSet,
          disableSidePanel,
        }}
      >
        {children}
      </ProjectInstructionContext.Provider>
    );
  },
);
